<template>
  <div class="page-view">

    <BasePageHeader :title="$t('customers.title')" @back="$router.back()">
      <template #actions>
        <router-link v-if="isManagerOrAbove" to="/bulk-edit/customers" class="btn ghost small">{{ $t('bulkEdit.title') }}</router-link>
        <button
          class="btn ghost small"
          :title="sortDir === 'asc' ? $t('customers.sortAZ') : $t('customers.sortZA')"
          @click="sortDir = sortDir === 'asc' ? 'desc' : 'asc'"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4">
            <path v-if="sortDir === 'asc'" d="M3 9l4-4 4 4M7 5v14M13 15l4 4 4-4M17 5v14"/>
            <path v-else d="M3 15l4 4 4-4M7 19V5M13 9l4-4 4 4M17 19V5"/>
          </svg>
        </button>
        <button v-if="isManagerOrAbove" class="btn primary small" @click="openCreate">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" class="w-4 h-4"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          {{ $t('common.new') }}
        </button>
      </template>
    </BasePageHeader>

    <div class="px-4 pt-3.5 pb-2">
      <SearchField v-model="query" :debounce="0" :placeholder="$t('customers.searchPlaceholder')" />
    </div>

    <div v-if="formOpen" class="mx-4 my-1 p-4 bg-[var(--color-surface)] border border-gray-700 rounded-xl flex flex-col gap-2.5">
      <div class="text-sm font-semibold text-gray-200">{{ editing ? $t('customers.editCustomer') : $t('customers.newCustomer') }}</div>
      <BaseFormField :label="$t('common.name')" required>
        <BaseInput v-model="form.name" :placeholder="$t('customers.namePlaceholder')" />
      </BaseFormField>
      <BaseFormField :label="$t('checkout.email')">
        <BaseInput v-model="form.email" :placeholder="$t('checkout.email')" type="email" />
      </BaseFormField>
      <BaseFormField :label="$t('checkout.phone')">
        <BaseInput v-model="form.phone" :placeholder="$t('checkout.phone')" />
      </BaseFormField>
      <BaseFormField :label="$t('customers.address')">
        <BaseInput v-model="form.address" :placeholder="$t('customers.addressPlaceholder')" />
      </BaseFormField>
      <BaseFormField :label="$t('customers.contactPerson')">
        <BaseInput v-model="form.contact_person" :placeholder="$t('customers.contactPersonPlaceholder')" />
      </BaseFormField>
      <BaseFormActions
        :confirm-label="editing ? $t('common.update') : $t('common.create')"
        :loading="saving"
        :disabled="!form.name"
        @cancel="closeForm"
        @confirm="save"
      />
    </div>

    <EmptyState v-if="loading" :message="$t('common.loading')" />
    <EmptyState v-else-if="loadError" variant="error" :message="loadError" />
    <EmptyState
      v-else-if="!filteredSorted.length"
      :message="query ? $t('customers.noMatch') : $t('customers.noCustomers')"
    />

    <div v-else class="list-stack">
      <div v-for="c in filtered" :key="c.id" class="data-card cursor-pointer" @click="router.push(`/customers/${c.id}`)">
        <div class="data-card__main">
          <div class="data-card__name">{{ c.name }}</div>
          <div class="data-card__meta">
            <span v-if="c.email" class="badge badge-success">{{ c.email }}</span>
            <span v-if="c.phone" class="text-xs text-gray-500">{{ c.phone }}</span>
          </div>
          <div v-if="c.contact_person" class="text-xs text-gray-500 mt-0.5">{{ c.contact_person }}</div>
        </div>
        <div class="data-card__actions" @click.stop>
          <button v-if="isManagerOrAbove" class="btn-icon" :title="$t('common.edit')" @click="openEdit(c)">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4Z"/></svg>
          </button>
          <button v-if="isManagerOrAbove" class="btn-icon danger" :title="$t('common.delete')" @click="remove(c)">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/></svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="totalPages > 1" class="flex items-center justify-center gap-3 px-4 py-3 text-sm">
      <button class="btn ghost small" :disabled="currentPage === 1" @click="currentPage--">← {{ $t('common.prev') }}</button>
      <span class="text-[var(--color-muted)] text-xs">{{ $t('common.pageOf', { page: currentPage, total: totalPages }) }}</span>
      <button class="btn ghost small" :disabled="currentPage >= totalPages" @click="currentPage++">{{ $t('common.next') }} →</button>
    </div>

  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useToast } from '@/composables/useToast'
import { usePermissions } from '@/composables/usePermissions.js'
import { getCustomers, postCustomer, putCustomer, deleteCustomer } from '@/api/api'
import BasePageHeader from '@/components/base/BasePageHeader.vue'
import BaseFormField from '@/components/base/BaseFormField.vue'
import BaseInput from '@/components/base/BaseInput.vue'
import BaseFormActions from '@/components/base/BaseFormActions.vue'
import EmptyState from '@/components/base/EmptyState.vue'
import SearchField from '@/components/search/SearchField.vue'
import { useI18n } from 'vue-i18n'

const router = useRouter()

const toast = useToast()
const { isManagerOrAbove } = usePermissions()
const { t } = useI18n()
const query = ref('')
const sortDir = ref('asc')
const currentPage = ref(1)
const pageSize = 25
const customers = ref([])
const loading = ref(false)
const loadError = ref(null)
const formOpen = ref(false)
const editing = ref(null)
const saving = ref(false)
const form = ref({ name: '', email: '', phone: '', address: '', contact_person: '' })
let initialLoad

const filteredSorted = computed(() => {
  const q = query.value.trim().toLowerCase()
  let list = customers.value
  if (q) {
    list = list.filter(c =>
      c.name?.toLowerCase().includes(q) ||
      c.email?.toLowerCase().includes(q) ||
      c.phone?.toLowerCase().includes(q)
    )
  }
  return [...list].sort((a, b) => {
    const na = (a.name ?? '').toLowerCase()
    const nb = (b.name ?? '').toLowerCase()
    return sortDir.value === 'asc' ? na.localeCompare(nb) : nb.localeCompare(na)
  })
})

const totalPages = computed(() => Math.ceil(filteredSorted.value.length / pageSize))

const filtered = computed(() => {
  const start = (currentPage.value - 1) * pageSize
  return filteredSorted.value.slice(start, start + pageSize)
})

watch([query, sortDir], () => { currentPage.value = 1 })

async function load() {
  loading.value = true
  loadError.value = null
  try {
    const res = await getCustomers()
    customers.value = res.results ?? res ?? []
  } catch {
    loadError.value = t('customers.loadFailed')
  } finally {
    loading.value = false
  }
}

function openCreate() {
  editing.value = null
  form.value = { name: '', email: '', phone: '', address: '', contact_person: '' }
  formOpen.value = true
}

function openEdit(c) {
  editing.value = c
  form.value = { name: c.name, email: c.email ?? '', phone: c.phone ?? '', address: c.address ?? '', contact_person: c.contact_person ?? '' }
  formOpen.value = true
}

function closeForm() {
  formOpen.value = false
  editing.value = null
}

async function save() {
  if (saving.value) return
  saving.value = true
  try {
    // Finish the initial snapshot before applying a local mutation.
    await initialLoad
    if (editing.value) {
      const updated = await putCustomer({ id: editing.value.id, ...form.value })
      const idx = customers.value.findIndex(c => c.id === editing.value.id)
      if (idx !== -1) customers.value[idx] = updated
      toast.success(t('customers.updated'))
    } else {
      const created = await postCustomer(form.value)
      const existing = customers.value.findIndex(c => c.id === created.id)
      if (existing === -1) customers.value.unshift(created)
      else customers.value[existing] = created
      toast.success(t('customers.created'))
    }
    closeForm()
  } catch {
    toast.error(t('customers.saveFailed'))
  } finally {
    saving.value = false
  }
}

async function remove(c) {
  if (!confirm(t('customers.deleteConfirm', { name: c.name }))) return
  try {
    await deleteCustomer(c)
    customers.value = customers.value.filter(x => x.id !== c.id)
    toast.success(t('customers.deleted'))
  } catch {
    toast.error(t('customers.deleteFailed'))
  }
}

onMounted(() => { initialLoad = load() })
</script>

